import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CloseIcon from '@mui/icons-material/Close'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useEffect, useRef, useState } from 'react'
import type { FundingNeedDraft } from '@/models/FundingNeeds'
import { AccessibleTooltip } from '../AccessibleTooltip'
import {
  startSuggestionsJob,
  pollSuggestionsJob,
  generateDraftRequest,
  cancelFundingIdeasJob,
  checkFundingIdeasUsage,
  UsageLimitError,
} from '@/api/fundingIdeasAPI'
import type { SuggestionItem } from '@/api/fundingIdeasAPI'
import UpgradeCTA from '@/components/UpgradeCTA'

interface FundingIdeaSuggestionsDrawerProps {
  open: boolean
  onClose: () => void
  onSelectDraft: (draft: FundingNeedDraft) => void
  token: string
  orgUuid: string
  orgAcceptsGambling?: boolean
  orgAcceptsInternational?: boolean
}

type FilterChoice = 'include' | 'keep'

type ConversationStep =
  | 'start'
  | 'filters'
  | 'scanning'
  | 'suggestions'
  | 'interest'

const START_IDEAS_LABEL = 'Show me ideas'

const CATEGORY_CONFIG: Record<
  string,
  { label: string; color: string; border: string }
> = {
  'strong-match': {
    label: 'Strong match',
    color: '#e8f5e9',
    border: '#a5d6a7',
  },
  adjacent: {
    label: 'Adjacent idea',
    color: '#fff3e0',
    border: '#ffcc80',
  },
  niche: {
    label: 'Niche opportunity',
    color: '#f3e5f5',
    border: '#ce93d8',
  },
  innovative: {
    label: 'New direction',
    color: '#e3f2fd',
    border: '#90caf9',
  },
}

const STEP_ORDER: ConversationStep[] = [
  'start',
  'filters',
  'scanning',
  'suggestions',
  'interest',
]

function AssistantMessage({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
      <Paper sx={{ px: 2, py: 1.5, maxWidth: '90%', bgcolor: '#edf2f7' }}>
        {children}
      </Paper>
    </Box>
  )
}

function UserMessage({ text }: { text: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
      <Paper sx={{ px: 2, py: 1, bgcolor: '#1C3045', color: 'white' }}>
        <Typography variant="body2">{text}</Typography>
      </Paper>
    </Box>
  )
}

const optionButtonSx = {
  textTransform: 'none' as const,
  borderColor: '#1C3045',
  color: '#1C3045',
  bgcolor: 'white',
  justifyContent: 'flex-start',
  '&:hover': {
    borderColor: '#1C3045',
    bgcolor: 'rgba(28, 48, 69, 0.04)',
  },
}

export default function FundingIdeaSuggestionsDrawer({
  open,
  onClose,
  onSelectDraft,
  token,
  orgUuid,
  orgAcceptsGambling,
  orgAcceptsInternational,
}: FundingIdeaSuggestionsDrawerProps) {
  const [step, setStep] = useState<ConversationStep>('start')
  const [filterChoice, setFilterChoice] = useState<FilterChoice | null>(null)
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<SuggestionItem | null>(null)
  const [interestReason, setInterestReason] = useState('')
  const [scanProgress, setScanProgress] = useState(0)
  const [jobId, setJobId] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<number | null>(null)
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [usageCheckStatus, setUsageCheckStatus] = useState<
    'loading' | 'ok' | 'blocked'
  >('loading')
  const [usageLimitError, setUsageLimitError] =
    useState<UsageLimitError | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  // Tracks whether a scan has been started so we skip the usage check on reopen
  const inActiveFlowRef = useRef(false)

  const hasGamblingFilter = orgAcceptsGambling !== true
  const hasInternationalFilter = orgAcceptsInternational !== true
  const hasFilters = hasGamblingFilter || hasInternationalFilter

  const currentStepIndex = STEP_ORDER.indexOf(step)
  const isStepPast = (s: ConversationStep) =>
    STEP_ORDER.indexOf(s) < currentStepIndex

  const filterText = (() => {
    const parts: string[] = []
    if (hasGamblingFilter) parts.push('gambling')
    if (hasInternationalFilter) parts.push('international')
    return parts.join(' and ')
  })()

  // Auto-scroll to bottom when step changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [step, scanProgress])

  // Poll the job while scanning
  useEffect(() => {
    if (step !== 'scanning' || !jobId) return

    const poll = setInterval(async () => {
      try {
        const job = await pollSuggestionsJob(token, jobId)
        setScanProgress(job.progress)
        if (job.status === 'completed') {
          clearInterval(poll)
          setSuggestions(Array.isArray(job.suggestions) ? job.suggestions : [])
          setSessionId(job.session_id ?? null)
          setStep('suggestions')
        } else if (job.status === 'error') {
          clearInterval(poll)
          setError(job.error || 'Something went wrong. Please try again.')
        }
      } catch {
        clearInterval(poll)
        setError('Something went wrong. Please try again.')
      }
    }, 2000)

    return () => clearInterval(poll)
  }, [step, jobId, token])

  // Check usage limit when drawer opens, but skip if already in an active flow
  useEffect(() => {
    if (!open) return
    if (inActiveFlowRef.current) return
    setUsageCheckStatus('loading')
    setUsageLimitError(null)
    checkFundingIdeasUsage(token, orgUuid)
      .then(() => setUsageCheckStatus('ok'))
      .catch((e) => {
        if (e instanceof UsageLimitError) {
          if (e.latest_session?.suggestions?.length) {
            // They're blocked from a new scan but can keep drafting from their latest results
            setSuggestions(e.latest_session.suggestions)
            setSessionId(e.latest_session.id)
            setStep('suggestions')
            inActiveFlowRef.current = true
            setUsageCheckStatus('ok')
            if (
              (e.latest_session.include_gambling && !orgAcceptsGambling) ||
              (e.latest_session.include_international &&
                !orgAcceptsInternational)
            ) {
              setFilterChoice('include')
            }
          } else {
            setUsageLimitError(e)
            setUsageCheckStatus('blocked')
          }
        } else {
          // Network error — let them through, drawer error handling covers it
          setUsageCheckStatus('ok')
        }
      })
  }, [open, token, orgUuid])

  const startJobAndScan = async (filterChoiceValue: FilterChoice | null) => {
    const include_gambling =
      filterChoiceValue === 'include' ? true : orgAcceptsGambling === true
    const include_international =
      filterChoiceValue === 'include' ? true : orgAcceptsInternational === true

    inActiveFlowRef.current = true
    setStep('scanning')
    setScanProgress(0)
    setError(null)

    try {
      const result = await startSuggestionsJob(token, {
        org_uuid: orgUuid,
        include_gambling,
        include_international,
      })
      setJobId(result.job_id)
    } catch (e) {
      if (e instanceof UsageLimitError) {
        // "Start over" is offered from a restored over-quota session, so this
        // is a reachable path: show the limit rather than a generic failure.
        // Clearing the ref lets the next open re-check once the quota resets,
        // and the step must leave 'scanning' or that reopen renders a frozen
        // scan with no job behind it.
        inActiveFlowRef.current = false
        setStep('start')
        setUsageLimitError(e)
        setUsageCheckStatus('blocked')
        return
      }
      setError('Something went wrong. Please try again.')
      setStep('start')
    }
  }

  const handleStart = () => {
    if (hasFilters) {
      setStep('filters')
    } else {
      startJobAndScan(null)
    }
  }

  const handleSelectFilter = (choice: FilterChoice) => {
    setFilterChoice(choice)
    startJobAndScan(choice)
  }

  const handleSelectSuggestion = (suggestion: SuggestionItem) => {
    if (selectedSuggestion?.title === suggestion.title && step === 'interest') {
      setSelectedSuggestion(null)
      setInterestReason('')
      setStep('suggestions')
      return
    }
    setSelectedSuggestion(suggestion)
    setStep('interest')
  }

  const handleConfirmCancel = async () => {
    if (!jobId) return
    try {
      await cancelFundingIdeasJob(token, jobId)
      handleReset()
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Failed to cancel generation. Please try again.',
      )
      setShowCancelConfirm(false)
    }
  }

  const handleSubmitInterest = async () => {
    if (!selectedSuggestion || !sessionId) return

    setIsSubmittingInterest(true)
    setError(null)

    try {
      // The response echoes the session's stored regions, and a session saved
      // before the location step was removed can still carry a custom list.
      // Drafts always use the org's current regions now, so drop them.
      const { region_ids: _legacySessionRegions, ...draftOutput } =
        await generateDraftRequest(token, {
          org_uuid: orgUuid,
          session_id: sessionId,
          suggestion_title: selectedSuggestion.title,
          interest_reason: interestReason,
        })

      const draft: FundingNeedDraft = {
        ...draftOutput,
        title: selectedSuggestion.title,
        funding_ideas_session_id: sessionId,
        accepts_gambling_proceeds:
          filterChoice === 'include' ? true : orgAcceptsGambling,
        accepts_international:
          filterChoice === 'include' ? true : orgAcceptsInternational,
        uses_org_regions: true,
      }

      setSelectedSuggestion(null)
      setInterestReason('')
      setStep('suggestions')
      inActiveFlowRef.current = false
      onSelectDraft(draft)
    } catch {
      setError('Failed to generate draft. Please try again.')
    } finally {
      setIsSubmittingInterest(false)
    }
  }

  const handleReset = () => {
    inActiveFlowRef.current = false
    setStep('start')
    setFilterChoice(null)
    setSelectedSuggestion(null)
    setInterestReason('')
    setScanProgress(0)
    setJobId(null)
    setSessionId(null)
    setSuggestions([])
    setError(null)
    setUsageLimitError(null)
    setIsSubmittingInterest(false)
    setShowCancelConfirm(false)
  }

  const getFilterLabel = (choice: FilterChoice) =>
    choice === 'include'
      ? 'Yes, include them for more matches'
      : 'Keep current settings'

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 520 },
          p: 3,
          bgcolor: '#f8fafc',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AutoAwesomeIcon sx={{ color: '#1C3045', mr: 1 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">
              What could you get funded to do?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Let&apos;s find the most fundable ideas for your organisation.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Error banner */}
        {error && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: '#fff3f3',
              border: '1px solid #ffcdd2',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}

        {/* Usage limit blocked state */}
        {usageCheckStatus === 'loading' && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={28} sx={{ color: '#1C3045' }} />
          </Box>
        )}

        {usageCheckStatus === 'blocked' && usageLimitError && (
          <Box
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Box
              sx={{
                p: 1.5,
                bgcolor: '#fff3f3',
                border: '1px solid #ffcdd2',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="error">
                {usageLimitError.type === 'usage_limit_free' ? (
                  "You've used your free limit for this feature. Upgrade to a paid plan for monthly access, or to an annual plan for unlimited access."
                ) : (
                  <>
                    Monthly subscribers can generate one AI-assisted funding
                    idea per month, and you’ve already used yours. Upgrade to an
                    annual plan for unlimited access.
                  </>
                )}
              </Typography>
            </Box>
            <UpgradeCTA
              variant="inline"
              message={
                usageLimitError.type === 'usage_limit_free'
                  ? undefined
                  : 'Upgrade to an annual plan for unlimited access.'
              }
              restrictedFeature={
                usageLimitError.type === 'usage_limit_free'
                  ? 'Funding Ideas Generator'
                  : undefined
              }
            />
          </Box>
        )}

        {/* Conversation area */}
        {usageCheckStatus === 'ok' && (
          <Box
            ref={scrollRef}
            role="log"
            aria-label="Funding ideas conversation"
            aria-live="polite"
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              pb: 2,
            }}
          >
            {/* Step 1: Start */}
            <AssistantMessage>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Let&apos;s explore what you could get funded to do.
              </Typography>
              {step === 'start' && (
                <Button
                  variant="outlined"
                  size="small"
                  disableRipple
                  onClick={handleStart}
                  sx={optionButtonSx}
                >
                  {START_IDEAS_LABEL}
                </Button>
              )}
            </AssistantMessage>

            {isStepPast('start') && <UserMessage text={START_IDEAS_LABEL} />}

            {/* Step 3: Filters (conditional) */}
            {hasFilters && (step === 'filters' || isStepPast('filters')) && (
              <>
                <AssistantMessage>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Your current settings exclude <strong>{filterText}</strong>{' '}
                    funds, but including these could result in more funding
                    matches. Are you open to accepting {filterText} funds?
                  </Typography>
                  {step === 'filters' && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        disableRipple
                        onClick={() => handleSelectFilter('include')}
                        sx={optionButtonSx}
                      >
                        Yes, include them
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        disableRipple
                        onClick={() => handleSelectFilter('keep')}
                        sx={optionButtonSx}
                      >
                        Keep current settings
                      </Button>
                    </Stack>
                  )}
                </AssistantMessage>

                {filterChoice && isStepPast('filters') && (
                  <UserMessage text={getFilterLabel(filterChoice)} />
                )}
              </>
            )}

            {/* Step 4: Scanning */}
            {step === 'scanning' && (
              <AssistantMessage>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Analysing real funding opportunities to generate ideas
                  related to your mission with available grants you could apply
                  for...
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <CircularProgress color="inherit" size={16} />
                  <LinearProgress
                    variant="determinate"
                    value={scanProgress}
                    sx={{
                      flex: 1,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { bgcolor: '#1C3045' },
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Generating ideas... {scanProgress}%
                </Typography>
              </AssistantMessage>
            )}

            {/* Step 5: Suggestions */}
            {(step === 'suggestions' || step === 'interest') && (
              <>
                <AssistantMessage>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Based on your profile, here are ideas ranked by funding
                    potential — from close matches to new directions. Pick one
                    to develop.
                  </Typography>
                </AssistantMessage>

                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1.5,
                    }}
                  >
                    {(Array.isArray(suggestions) ? suggestions : []).map(
                      (suggestion, index) => {
                        const isSelected =
                          selectedSuggestion?.title === suggestion.title
                        return (
                          <Paper
                            key={index}
                            onClick={() => handleSelectSuggestion(suggestion)}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: isSelected
                                ? '2px solid #1C3045'
                                : '1px solid #e0e0e0',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              opacity:
                                step === 'interest' && !isSelected ? 0.4 : 1,
                              '&:hover': {
                                borderColor: '#1C3045',
                                bgcolor: 'rgba(28, 48, 69, 0.02)',
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, mb: 0.5 }}
                            >
                              {suggestion.title}
                            </Typography>
                          </Paper>
                        )
                      },
                    )}
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                >
                  Want more matches?
                  <AccessibleTooltip title="You may wish to review your locations settings if you'd consider broadening where you deliver programmes to find more funding matches">
                    <span
                      role="img"
                      aria-label="Tip: you may wish to review your locations settings if you'd consider broadening where you deliver programmes to find more funding matches"
                      tabIndex={0}
                      style={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                      <HelpOutlineIcon
                        sx={{ fontSize: '18px', ml: 0.5, color: 'gray' }}
                      />
                    </span>
                  </AccessibleTooltip>
                </Typography>

                {selectedSuggestion && (
                  <UserMessage text={selectedSuggestion.title} />
                )}
              </>
            )}

            {/* Step 6: Interest */}
            {step === 'interest' && selectedSuggestion && (
              <AssistantMessage>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Great choice! Why are you interested in{' '}
                  <strong>&ldquo;{selectedSuggestion.title}&rdquo;</strong>?
                </Typography>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={4}
                  fullWidth
                  placeholder="Tell us what excites you about this idea..."
                  value={interestReason}
                  onChange={(e) => setInterestReason(e.target.value)}
                  sx={{ bgcolor: 'white', mb: 1.5 }}
                  disabled={isSubmittingInterest}
                />
                <Button
                  variant="contained"
                  disableRipple
                  onClick={handleSubmitInterest}
                  disabled={
                    isSubmittingInterest ||
                    !interestReason ||
                    interestReason.trim() === ''
                  }
                  startIcon={
                    isSubmittingInterest ? (
                      <CircularProgress size={16} sx={{ color: 'white' }} />
                    ) : undefined
                  }
                  sx={{ textTransform: 'none', bgcolor: '#1C3045' }}
                >
                  {isSubmittingInterest
                    ? 'Generating draft...'
                    : 'Develop this idea'}
                </Button>
              </AssistantMessage>
            )}
          </Box>
        )}

        {/* Footer */}
        {usageCheckStatus === 'ok' && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              pt: 1,
              borderTop: '1px solid #e0e0e0',
              alignItems: 'center',
            }}
          >
            {step === 'scanning' && showCancelConfirm ? (
              <>
                <Typography variant="body2" sx={{ mr: 1 }}>
                  Cancel the scan?
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disableRipple
                  onClick={handleConfirmCancel}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#d32f2f',
                    color: '#d32f2f',
                  }}
                >
                  Yes, cancel
                </Button>
                <Button
                  size="small"
                  disableRipple
                  onClick={() => setShowCancelConfirm(false)}
                  sx={{ textTransform: 'none', color: '#1C3045' }}
                >
                  Keep generating
                </Button>
              </>
            ) : step === 'scanning' ? (
              <Button
                variant="outlined"
                size="small"
                disableRipple
                onClick={() => setShowCancelConfirm(true)}
                sx={{
                  textTransform: 'none',
                  borderColor: '#1C3045',
                  color: '#1C3045',
                }}
              >
                Cancel
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                disableRipple
                onClick={handleReset}
                sx={{
                  textTransform: 'none',
                  borderColor: '#1C3045',
                  color: '#1C3045',
                }}
              >
                Start over
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  )
}
